/**
 * CSRF Token Management Service
 * Handles fetching, storing, and managing CSRF tokens for cross-site request forgery protection
 */

import type { CSRFTokenResponse, CSRFTokenInfo, CSRFConfig, CSRFError } from '@/types/api'

export class CSRFService {
  private config: CSRFConfig
  private storageKeys = {
    token: 'csrf_token',
    expiresAt: 'csrf_expires_at',
    issuedAt: 'csrf_issued_at'
  }

  constructor(config?: Partial<CSRFConfig>) {
    this.config = {
      enabled: import.meta.env.VITE_CSRF_ENABLED === 'true',
      headerName: import.meta.env.VITE_CSRF_HEADER_NAME || 'X-CSRF-Token',
      cookieName: import.meta.env.VITE_CSRF_COOKIE_NAME || 'csrf_token',
      refreshOnError: import.meta.env.VITE_CSRF_TOKEN_REFRESH_ON_ERROR === 'true',
      excludeMethods: (import.meta.env.VITE_CSRF_EXCLUDE_METHODS || 'GET,HEAD,OPTIONS').split(','),
      ...config
    }
  }

  /**
   * Check if CSRF protection is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled
  }

  /**
   * Get current CSRF configuration
   */
  getConfig(): CSRFConfig {
    return { ...this.config }
  }

  /**
   * Update CSRF configuration
   */
  updateConfig(newConfig: Partial<CSRFConfig>): void {
    this.config = { ...this.config, ...newConfig }
  }

  /**
   * Check if HTTP method requires CSRF protection
   */
  requiresCSRFToken(method: string): boolean {
    if (!this.config.enabled) return false
    return !this.config.excludeMethods.includes(method.toUpperCase())
  }

  /**
   * Get stored CSRF token information
   */
  getStoredTokenInfo(): CSRFTokenInfo | null {
    try {
      const token = localStorage.getItem(this.storageKeys.token)
      const expiresAtStr = localStorage.getItem(this.storageKeys.expiresAt)
      const issuedAtStr = localStorage.getItem(this.storageKeys.issuedAt)

      if (!token || !issuedAtStr) return null

      const issuedAt = parseInt(issuedAtStr, 10)
      const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : undefined

      return {
        token,
        expiresAt,
        issuedAt
      }
    } catch (error) {
      console.error('Failed to get stored CSRF token info:', error)
      return null
    }
  }

  /**
   * Get current CSRF token
   */
  getToken(): string | null {
    const tokenInfo = this.getStoredTokenInfo()
    return tokenInfo?.token || null
  }

  /**
   * Check if CSRF token is expired
   */
  isTokenExpired(bufferMinutes: number = 5): boolean {
    const tokenInfo = this.getStoredTokenInfo()
    if (!tokenInfo) return true

    // If no expiration time, consider token valid
    if (!tokenInfo.expiresAt) return false

    const now = Date.now()
    const bufferMs = bufferMinutes * 60 * 1000
    return now >= (tokenInfo.expiresAt - bufferMs)
  }

  /**
   * Check if CSRF token should be refreshed
   */
  shouldRefreshToken(refreshMinutesBefore: number = 10): boolean {
    const tokenInfo = this.getStoredTokenInfo()
    if (!tokenInfo) return true

    // If no expiration time, don't refresh
    if (!tokenInfo.expiresAt) return false

    const now = Date.now()
    const refreshTime = tokenInfo.expiresAt - (refreshMinutesBefore * 60 * 1000)
    return now >= refreshTime
  }

  /**
   * Store CSRF token information
   */
  storeToken(token: string, expiresIn?: number): void {
    try {
      const now = Date.now()
      
      localStorage.setItem(this.storageKeys.token, token)
      localStorage.setItem(this.storageKeys.issuedAt, now.toString())
      
      if (expiresIn) {
        const expiresAt = now + (expiresIn * 1000)
        localStorage.setItem(this.storageKeys.expiresAt, expiresAt.toString())
      } else {
        localStorage.removeItem(this.storageKeys.expiresAt)
      }
    } catch (error) {
      console.error('Failed to store CSRF token:', error)
      throw new Error('Failed to store CSRF token')
    }
  }

  /**
   * Update CSRF token from response
   */
  updateTokenFromResponse(response: CSRFTokenResponse): void {
    this.storeToken(response.csrf_token, response.expires_in)
  }

  /**
   * Clear stored CSRF token
   */
  clearToken(): void {
    try {
      localStorage.removeItem(this.storageKeys.token)
      localStorage.removeItem(this.storageKeys.expiresAt)
      localStorage.removeItem(this.storageKeys.issuedAt)
    } catch (error) {
      console.error('Failed to clear CSRF token:', error)
    }
  }

  /**
   * Fetch new CSRF token from server
   */
  async fetchToken(): Promise<CSRFTokenResponse> {
    try {
      // Import axios here to avoid circular dependency
      const { default: axios } = await import('axios')
      
      const response = await axios.get('/auth/csrf-token', {
        baseURL: import.meta.env.VITE_API_BASE_URL || 'https://commutix-server.ddev.site/api'
      })

      if (!response.data?.csrf_token) {
        throw new Error('Invalid CSRF token response')
      }

      return response.data as CSRFTokenResponse
    } catch (error: any) {
      console.error('Failed to fetch CSRF token:', error)
      throw new Error(`Failed to fetch CSRF token: ${error.message}`)
    }
  }

  /**
   * Refresh CSRF token
   */
  async refreshToken(): Promise<CSRFTokenResponse> {
    try {
      const response = await this.fetchToken()
      this.updateTokenFromResponse(response)
      return response
    } catch (error) {
      console.error('Failed to refresh CSRF token:', error)
      throw error
    }
  }

  /**
   * Get CSRF token for request (fetch if needed)
   */
  async getTokenForRequest(): Promise<string | null> {
    if (!this.config.enabled) return null

    let tokenInfo = this.getStoredTokenInfo()
    
    // If no token or token should be refreshed, fetch new one
    if (!tokenInfo || this.shouldRefreshToken()) {
      try {
        await this.refreshToken()
        tokenInfo = this.getStoredTokenInfo()
      } catch (error) {
        console.error('Failed to get CSRF token for request:', error)
        return null
      }
    }

    return tokenInfo?.token || null
  }

  /**
   * Get CSRF headers for request
   */
  async getHeaders(): Promise<Record<string, string>> {
    if (!this.config.enabled) return {}

    const token = await this.getTokenForRequest()
    if (!token) return {}

    return {
      [this.config.headerName]: token
    }
  }

  /**
   * Check if error is CSRF-related
   */
  isCSRFError(error: any): boolean {
    if (!error?.response) return false
    
    const status = error.response.status
    const data = error.response.data
    
    // Check for 419 status (CSRF token mismatch) or specific error codes
    if (status === 419) return true
    
    // Check for CSRF-related error messages or codes
    if (data?.code && ['CSRF_TOKEN_MISMATCH', 'CSRF_TOKEN_MISSING', 'CSRF_TOKEN_EXPIRED'].includes(data.code)) {
      return true
    }
    
    // Check for common CSRF error messages
    const message = data?.message?.toLowerCase() || ''
    return message.includes('csrf') || message.includes('token mismatch') || message.includes('forbidden')
  }

  /**
   * Handle CSRF error by refreshing token
   */
  async handleCSRFError(): Promise<string | null> {
    if (!this.config.refreshOnError) return null

    try {
      const response = await this.refreshToken()
      return response.csrf_token
    } catch (error) {
      console.error('Failed to handle CSRF error:', error)
      return null
    }
  }

  /**
   * Extract CSRF token from cookie (fallback method)
   */
  getTokenFromCookie(): string | null {
    if (typeof document === 'undefined') return null

    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === this.config.cookieName) {
        return decodeURIComponent(value)
      }
    }
    return null
  }

  /**
   * Validate CSRF token format
   */
  isValidTokenFormat(token: string): boolean {
    if (!token || typeof token !== 'string') return false
    
    // Basic validation - token should be non-empty and reasonable length
    return token.length >= 16 && token.length <= 256
  }

  /**
   * Get service status
   */
  getStatus(): {
    enabled: boolean
    hasToken: boolean
    isExpired: boolean
    shouldRefresh: boolean
  } {
    const tokenInfo = this.getStoredTokenInfo()
    
    return {
      enabled: this.config.enabled,
      hasToken: !!tokenInfo?.token,
      isExpired: this.isTokenExpired(),
      shouldRefresh: this.shouldRefreshToken()
    }
  }
}

// Create and export default CSRF service instance
export const csrfService = new CSRFService()

// Export factory function for custom configurations
export function createCSRFService(config?: Partial<CSRFConfig>): CSRFService {
  return new CSRFService(config)
}
